import React, { createContext, useEffect, useState } from 'react';

import { DesignStudio } from 'src/shared/constants/FeatureFlag';
import { useSplit } from 'src/shared/hooks';

enum Mode {
  DEFAULT = 'default',
  GUEST = 'guest',
  PRO_VIEW = 'proView',
  SHARE = 'share',
}

enum Partner {
  DEFAULT = 'default',
  JAMES_HARDIE = 'james-hardie',
}

const defaultContext = {
  mode: Mode.DEFAULT,
  partner: Partner.DEFAULT,
};

const PartnerIdMapping: { [key: number]: Partner } = {
  9: Partner.JAMES_HARDIE,
};

const mapPartnerIdToEnum = (id: number) => {
  if (PartnerIdMapping[id]) {
    return PartnerIdMapping[id];
  }
  return Partner.DEFAULT;
};

const context = createContext<{
  mode: Mode;
  partner: Partner;
}>(defaultContext);

const validatePartner = (query?: string): Partner => {
  const lowerCaseQuery = query?.toLowerCase();
  const isValidPartner = Object.values(Partner).includes(
    lowerCaseQuery as Partner,
  );
  if (isValidPartner) {
    return lowerCaseQuery as Partner;
  }
  return Partner.DEFAULT;
};

const determineInitialPartner = (partnerQuery?: string, partnerId?: number) => {
  if (partnerQuery) {
    return validatePartner(partnerQuery);
  }
  return partnerId ? mapPartnerIdToEnum(partnerId) : Partner.DEFAULT;
};

const Provider = ({
  mode,
  partnerQuery,
  partnerId,
  children,
}: {
  mode: Mode;
  partnerQuery?: string;
  partnerId?: number;
  children: React.ReactNode;
}) => {
  const [partner, setPartner] = useState(
    determineInitialPartner(partnerQuery, partnerId),
  );
  const { client } = useSplit();

  useEffect(() => {
    const handleSetPartner = async () => {
      const splitClient = await client;
      const shouldSetPartner =
        splitClient.getTreatment(DesignStudio.DS_JH_PRODUCTS) === 'on';

      if (mode !== Mode.GUEST && shouldSetPartner && partnerId) {
        setPartner(mapPartnerIdToEnum(partnerId));
      }
    };
    void handleSetPartner();
  }, [mode, setPartner, partnerId, client]);

  return (
    <context.Provider
      value={{
        mode,
        partner,
      }}
    >
      {children}
    </context.Provider>
  );
};

export { Provider, context, Mode, Partner, mapPartnerIdToEnum };
